import * as React from 'react';

import {
  type ZonedDateTime,
  getLocalTimeZone,
  now,
} from '@internationalized/date';
import {
  CurrencyCode,
  enumCurrencyCode,
  enumPaymentMethod,
  enumPaymentTerm,
  PaymentMethod,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  cn,
  Col,
  ConsoleTouchDialog,
  DatePicker,
  Row,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import {
  LucidePlus,
  LucideReceipt,
  LucideSplitSquareHorizontal,
} from 'lucide-react';

import { $updatePurchaseLoadFromModal } from '../../../_state/scaleTicketPro.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { PurchaseLoadPaymentRecord } from '../../../types/purchaseLoad';
import { allocate } from '../../../utils/allocate';
import { useDialogModalStyle } from '../../_shared/ItemsInterface/hooks/useDialogModalStyle';

import {
  isImmediatePaymentTerm,
  PaymentInstallment,
  PaymentInstallmentData,
} from './PaymentInstallment';

import { useTheme, useUIPreferences } from '~/shared/hooks';
import { maybeParseZonedDateTime } from '~/shared/utils/dateTime';

type FinishScaleTicketModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  grandTotal: string;
  currencyCode?: CurrencyCode;
  isProcessing: boolean;
  facilityId: string;
  purchaseLoadId: string;
  existingPayments?: PurchaseLoadPaymentRecord[];
  ticketReceivedDate?: string;
};

function parseTotalAmount(grandTotal: string): number {
  return parseFloat(grandTotal.replace(/[^0-9.-]/g, '')) || 0;
}

function mapFromApiPayment(
  payment: PurchaseLoadPaymentRecord,
  totalAmount: number,
): PaymentInstallmentData {
  return {
    id: payment.id,
    paymentMethod: payment.paymentMethod as PaymentMethod,
    paymentTerm: payment.paymentTerm as PaymentInstallmentData['paymentTerm'],
    amount: payment.amount.amount,
    percentage:
      payment.percentage ??
      (totalAmount > 0
        ? Math.round((payment.amount.amount / totalAmount) * 100 * 100) / 100
        : 100),
    legalHold: payment.legalHold,
    financialAccountId: payment.financialAccountId ?? undefined,
    checkNumber: payment.checkNumber ?? undefined,
    checkMemo: payment.checkMemo ?? undefined,
  };
}

function computeValidationErrors(
  installments: PaymentInstallmentData[],
  totalAmount: number,
  t: ReturnType<typeof useTranslations>,
): string[] {
  const errors: string[] = [];

  for (const inst of installments) {
    const n = installments.indexOf(inst) + 1;
    if (inst.amount <= 0) {
      errors.push(t.scaleTicketPro.finishModal.errors.amountZero(n));
    }
    if (isImmediatePaymentTerm(inst.paymentTerm) && !inst.financialAccountId) {
      errors.push(
        t.scaleTicketPro.finishModal.errors.financialAccountRequired(n),
      );
    }
    if (
      inst.paymentMethod === enumPaymentMethod.Check &&
      isImmediatePaymentTerm(inst.paymentTerm) &&
      !inst.checkNumber
    ) {
      errors.push(t.scaleTicketPro.finishModal.errors.checkNumberRequired(n));
    }
  }

  const checksByAccount = new Map<string, string[]>();
  for (const inst of installments) {
    if (
      inst.paymentMethod === enumPaymentMethod.Check &&
      isImmediatePaymentTerm(inst.paymentTerm) &&
      inst.checkNumber &&
      inst.financialAccountId
    ) {
      const existing = checksByAccount.get(inst.financialAccountId) ?? [];
      existing.push(inst.checkNumber);
      checksByAccount.set(inst.financialAccountId, existing);
    }
  }
  const hasDuplicateCheckNumbers = [...checksByAccount.values()].some(
    (nums) => new Set(nums).size !== nums.length,
  );
  if (hasDuplicateCheckNumbers) {
    errors.push(t.scaleTicketPro.finishModal.errors.duplicateCheckNumber);
  }

  const sum = installments.reduce((acc, inst) => acc + inst.amount, 0);
  if (sum - totalAmount > 0.01) {
    errors.push(t.scaleTicketPro.finishModal.errors.totalExceeded);
  }

  return errors;
}

let localIdCounter = 0;

//
// ⚛️ ⚛️ ⚛️ FINISH SCALE TICKET MODAL ⚛️ ⚛️ ⚛️
//
export const FinishScaleTicketModal = ({
  isOpen,
  onClose,
  onConfirm,
  grandTotal,
  isProcessing,
  facilityId,
  purchaseLoadId,
  existingPayments,
  currencyCode = enumCurrencyCode.USD,
  ticketReceivedDate,
}: FinishScaleTicketModalProps) => {
  // Translations
  const t = useTranslations();

  // Refs
  const isInitializedRef = React.useRef(false);
  const serverPaymentIdsRef = React.useRef<Set<string>>(new Set());

  // States
  const [installments, setInstallments] = React.useState<
    PaymentInstallmentData[]
  >([]);
  const [isInitializing, setIsInitializing] = React.useState(false);
  const [isSavingPayments, setIsSavingPayments] = React.useState(false);
  const [localReceivedDate, setLocalReceivedDate] =
    React.useState<ZonedDateTime | null>(
      ticketReceivedDate
        ? (maybeParseZonedDateTime(ticketReceivedDate) ?? null)
        : null,
    );

  // Atoms
  const updatePurchaseLoad = useSetAtom($updatePurchaseLoadFromModal);

  // Other hooks
  const { effectiveColorScheme } = useTheme();
  const { touchModeEnabled } = useUIPreferences();
  const modalStyle = useDialogModalStyle();
  const toast = useToast();

  // Memoized values
  const totalAmount = React.useMemo(
    () => parseTotalAmount(grandTotal),
    [grandTotal],
  );
  const combinedProcessing = isProcessing || isSavingPayments;

  const validationErrors = React.useMemo(
    () => computeValidationErrors(installments, totalAmount, t),
    [installments, totalAmount, t],
  );

  const isValid = validationErrors.length === 0;

  // Callbacks
  const persistPayments = React.useCallback(
    async (nextInstallments: PaymentInstallmentData[]) => {
      const payments = nextInstallments.map((inst) => {
        const isServerPayment = serverPaymentIdsRef.current.has(inst.id);
        return {
          id: isServerPayment ? inst.id : undefined,
          paymentMethod: inst.paymentMethod,
          paymentTerm: inst.paymentTerm,
          amount: { amount: inst.amount, currencyCode },
          percentage: inst.percentage,
          legalHold: inst.legalHold,
          financialAccountId: inst.financialAccountId ?? null,
          checkNumber:
            inst.paymentMethod === enumPaymentMethod.Check
              ? (inst.checkNumber ?? null)
              : null,
          checkMemo:
            inst.paymentMethod === enumPaymentMethod.Check
              ? (inst.checkMemo ?? null)
              : null,
        };
      });

      const result = await updatePurchaseLoad({
        id: purchaseLoadId,
        input: { payments },
      });

      const returnedPayments = result.tradePurchaseLoadUpdate?.payments ?? [];
      serverPaymentIdsRef.current = new Set(
        returnedPayments.map((p: { id: string }) => p.id),
      );
      setInstallments(
        returnedPayments.map(
          (p: {
            id: string;
            paymentMethod: string;
            paymentTerm: string;
            amount: { amount: number };
            percentage: number | null;
            legalHold: boolean;
            financialAccountId: string | null;
            checkNumber: string | null;
            checkMemo: string | null;
          }) => ({
            id: p.id,
            paymentMethod: p.paymentMethod as PaymentMethod,
            paymentTerm: p.paymentTerm as PaymentInstallmentData['paymentTerm'],
            amount: p.amount.amount,
            percentage:
              p.percentage ??
              (totalAmount > 0
                ? Math.round((p.amount.amount / totalAmount) * 100 * 100) / 100
                : 100),
            legalHold: p.legalHold,
            financialAccountId: p.financialAccountId ?? undefined,
            checkNumber: p.checkNumber ?? undefined,
            checkMemo: p.checkMemo ?? undefined,
          }),
        ),
      );
    },
    [currencyCode, updatePurchaseLoad, purchaseLoadId, totalAmount],
  );

  const initializePayments = React.useCallback(() => {
    setIsInitializing(true);
    try {
      serverPaymentIdsRef.current = new Set();
      if (existingPayments && existingPayments.length > 0) {
        for (const p of existingPayments) {
          serverPaymentIdsRef.current.add(p.id);
        }
        setInstallments(
          existingPayments.map((p) => mapFromApiPayment(p, totalAmount)),
        );
      } else {
        setInstallments([]);
      }
    } finally {
      setIsInitializing(false);
    }
  }, [existingPayments, totalAmount]);

  const handleTicketDateChange = React.useCallback(
    async (value: ZonedDateTime | null) => {
      setLocalReceivedDate(value);
      if (value) {
        await updatePurchaseLoad({
          id: purchaseLoadId,
          input: { receivedDate: value.toAbsoluteString() },
        });
      }
    },
    [purchaseLoadId, updatePurchaseLoad],
  );

  const handleInstallmentChange = React.useCallback(
    (index: number, updated: PaymentInstallmentData) => {
      const next = [...installments];
      next[index] = updated;
      setInstallments(next);
    },
    [installments],
  );

  const handleDeleteInstallment = React.useCallback(
    (index: number) => {
      const next = installments.filter((_, i) => i !== index);
      setInstallments(next);
    },
    [installments],
  );

  const handleAddInstallment = React.useCallback(() => {
    const count = installments.length + 1;
    const ratios = Array.from({ length: count }, () => ({ ratio: 1 }));
    const amounts = allocate(totalAmount, 2, ratios);

    const newAmount = amounts[amounts.length - 1] ?? 0;
    localIdCounter++;
    const newInstallment: PaymentInstallmentData = {
      id: `local-${localIdCounter}`,
      paymentMethod: enumPaymentMethod.Cash,
      paymentTerm: enumPaymentTerm.COD,
      amount: newAmount,
      percentage:
        totalAmount > 0
          ? Math.round((newAmount / totalAmount) * 100 * 100) / 100
          : 0,
      legalHold: false,
    };

    const next = amounts.map((amount, i) => {
      const existing = installments[i];
      const percentage =
        totalAmount > 0
          ? Math.round((amount / totalAmount) * 100 * 100) / 100
          : 0;
      if (existing) {
        return { ...existing, amount, percentage };
      }
      return newInstallment;
    });

    setInstallments(next);
  }, [installments, totalAmount]);

  const handleSplitPayment = React.useCallback(() => {
    if (installments.length <= 1) return;

    const ratios = installments.map(() => ({ ratio: 1 }));
    const amounts = allocate(totalAmount, 2, ratios);

    const next = installments.map((inst, i) => {
      const amount = amounts[i] ?? 0;
      const percentage =
        totalAmount > 0
          ? Math.round((amount / totalAmount) * 100 * 100) / 100
          : 0;
      return { ...inst, amount, percentage };
    });

    setInstallments(next);
  }, [installments, totalAmount]);

  const handleConfirm = React.useCallback(async () => {
    setIsSavingPayments(true);
    try {
      await persistPayments(installments);
      await onConfirm();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t.scaleTicketPro.detail.toast.payError,
      );
    } finally {
      setIsSavingPayments(false);
    }
  }, [installments, onConfirm, persistPayments, t, toast]);

  // Effects
  /** Sync local received date from props */
  React.useEffect(() => {
    if (ticketReceivedDate) {
      setLocalReceivedDate(maybeParseZonedDateTime(ticketReceivedDate) ?? null);
    }
  }, [ticketReceivedDate]);

  /** Initialize payments when modal opens */
  React.useEffect(() => {
    if (!isOpen) {
      isInitializedRef.current = false;
      return;
    }
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;
    initializePayments();
  }, [isOpen, initializePayments]);

  return (
    <ConsoleTouchDialog
      touchModeEnabled={touchModeEnabled}
      effectiveColorScheme={effectiveColorScheme}
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      className={cn(
        '@FinishScaleTicketModal',
        '[&_.@DialogFooter]:border-none [&_.@DialogFooter]:bg-transparent',
      )}
      dialogClassName={cn('h-full w-full max-w-full !pb-0')}
      contentStyle={modalStyle}
      header={
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-success/10">
            <LucideReceipt className="h-5 w-5 text-success" />
          </div>
          <div className="text-base font-semibold">
            {t.scaleTicketPro.finishModal.title}
          </div>
        </div>
      }
      footer={
        <Row xBetween yCenter className="w-full gap-4">
          {validationErrors.length > 0 && !isInitializing && (
            <div className="flex-1 rounded-md border border-failure/30 bg-failure/5 px-3 py-2">
              <ul className="list-inside list-disc text-xs text-failure">
                {validationErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          <Button
            variant="solid"
            color="success"
            className="ml-auto shrink-0"
            onPress={handleConfirm}
            isLoading={combinedProcessing}
            isDisabled={combinedProcessing || isInitializing || !isValid}
          >
            {t.scaleTicketPro.finishModal.confirm}
          </Button>
        </Row>
      }
    >
      <Col gap4>
        <Row
          xBetween
          yEnd
          gap3
          className="rounded-lg border border-neutral/10 p-4"
        >
          <Row gap3 className="flex-1">
            <DatePicker
              label={t.scaleTicketPro.finishModal.ticketDate}
              value={localReceivedDate}
              onChange={handleTicketDateChange}
              maxValue={now(getLocalTimeZone())}
              granularity="minute"
            />
          </Row>
          <div className="text-right">
            <div className="mb-1 text-xs text-foreground/60">
              {t.scaleTicketPro.finishModal.grandTotal}
            </div>
            <div className="rounded-md bg-success/10 px-3 py-1">
              <div className="text-2xl font-bold text-success">
                {grandTotal}
              </div>
            </div>
          </div>
        </Row>

        <Col gap3>
          {installments.map((installment, index) => {
            let checkOffset = 0;
            for (let i = 0; i < index; i++) {
              const prev = installments[i];
              if (
                prev &&
                prev.paymentMethod === enumPaymentMethod.Check &&
                isImmediatePaymentTerm(prev.paymentTerm) &&
                prev.financialAccountId === installment.financialAccountId
              ) {
                checkOffset++;
              }
            }
            return (
              <PaymentInstallment
                key={installment.id}
                index={index}
                data={installment}
                totalAmount={totalAmount}
                currencyCode={currencyCode}
                facilityId={facilityId}
                canDelete
                checkNumberOffset={checkOffset}
                onChange={(updated) => handleInstallmentChange(index, updated)}
                onDelete={() => handleDeleteInstallment(index)}
              />
            );
          })}
        </Col>

        <Row gap2>
          <Button
            variant="outline"
            color="primary"
            onPress={handleAddInstallment}
          >
            <Row gap1>
              <LucidePlus size={16} />
              {t.scaleTicketPro.finishModal.addImmediatePayment}
            </Row>
          </Button>
          {installments.length > 1 && (
            <Button
              variant="outline"
              color="primary"
              onPress={handleSplitPayment}
            >
              <Row gap1>
                <LucideSplitSquareHorizontal size={16} />
                {t.scaleTicketPro.finishModal.splitPayment}
              </Row>
            </Button>
          )}
        </Row>
      </Col>
    </ConsoleTouchDialog>
  );
};
